import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box, Typography, Paper, Button, Chip, Alert, CircularProgress, TextField, MenuItem, Autocomplete, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';

import { kbAPI, usersAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');
const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '-');
const isOverdue = (d) => !!d && new Date(d) <= new Date(new Date().toDateString());
const errText = (err, fallback) => (err.response?.status === 401
  ? 'Your sign-in has expired. You are being taken to sign in again; if nothing happens, sign out and back in.'
  : err.response?.data?.detail || fallback);

// ── Rendering ──────────────────────────────────────────────────────────────

// Images stored with an article need the sign-in token, so fetch them and show them from memory
function ArticleImage({ articleId, src, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    if (/^https:\/\//i.test(src || '')) {
      setUrl(src);
    } else if (articleId && /^(\.\/)?assets\//.test(src || '')) {
      kbAPI.asset(articleId, src.replace(/^(\.\/)?assets\//, ''))
        .then((r) => { if (!cancelled) { objectUrl = URL.createObjectURL(r.data); setUrl(objectUrl); } })
        .catch(() => {});
    }
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [articleId, src]);
  return url ? <img src={url} alt={alt || ''} style={{ maxWidth: '100%' }} /> : <em>[{alt || 'image'}]</em>;
}

function Markdown({ text, articleId }) {
  return (
    <Box
      sx={{
        lineHeight: 1.7, wordBreak: 'break-word',
        '& h1': { fontSize: '1.9rem', mt: 3, mb: 1.5 }, '& h2': { fontSize: '1.5rem', mt: 3, mb: 1 }, '& h3': { fontSize: '1.2rem', mt: 2.5, mb: 1 },
        '& p': { my: 1.2 }, '& ul, & ol': { pl: 3 },
        '& pre': { bgcolor: '#0f1a24', color: '#dce7f1', p: 1.5, borderRadius: 1, overflowX: 'auto', fontSize: 13 },
        '& code': { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '0.9em' },
        '& :not(pre) > code': { bgcolor: 'action.hover', px: 0.6, borderRadius: 0.5 },
        '& blockquote': { m: 0, pl: 2, borderLeft: '4px solid', borderColor: 'divider', color: 'text.secondary' },
        '& table': { borderCollapse: 'collapse', my: 1.5 }, '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1.2, py: 0.6 },
        '& img': { maxWidth: '100%' },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          img: ({ src, alt }) => <ArticleImage articleId={articleId} src={src} alt={alt} />,
          a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
        }}
      >
        {text || ''}
      </ReactMarkdown>
    </Box>
  );
}

// "…matching [[word]] in a sentence…" -> highlighted words, safely (nothing is treated as HTML)
function Snippet({ text }) {
  if (!text) return null;
  const parts = text.split(/(\[\[.*?\]\])/g);
  return (
    <Typography variant="body2" color="text.secondary">
      {parts.map((p, i) => (p.startsWith('[[') ? <mark key={i}>{p.slice(2, -2)}</mark> : <span key={i}>{p}</span>))}
    </Typography>
  );
}

// ── Uploading Markdown files ───────────────────────────────────────────────

const MAX_UPLOAD_BYTES = 800 * 1024;
const MARKDOWN_FILE = /\.(md|markdown|txt)$/i;

function cleanTag(t) { return String(t).toLowerCase().replace(/[^a-z0-9 ._-]/g, '').trim().slice(0, 30); }
function cleanTags(list) {
  const out = [];
  (list || []).forEach((t) => { const c = cleanTag(t); if (c && !out.includes(c)) out.push(c); });
  return out.slice(0, 12);
}
const headerTags = (meta) => cleanTags((meta.tags || '').replace(/[[\]]/g, '').split(','));

function UploadDialog({ categories, onClose }) {
  const { email } = usePermissions();
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [status, setStatus] = useState('published');
  const [reviewDue, setReviewDue] = useState('');
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { kbAPI.tags().then((r) => setTagOptions(r.data.map((t) => t.tag))).catch(() => {}); }, []);

  const patch = (key, changes) => setFiles((all) => all.map((f) => (f.key === key ? { ...f, ...changes } : f)));

  const addFiles = async (list) => {
    const added = [];
    for (const file of Array.from(list || [])) {
      const item = { key: `${file.name}-${file.size}-${Math.random()}`, name: file.name, size: file.size, text: '', title: '', meta: {}, error: null, state: 'ready' };
      if (!MARKDOWN_FILE.test(file.name)) item.error = 'Only Markdown files (.md) can be uploaded.';
      else if (file.size > MAX_UPLOAD_BYTES) item.error = `Too large (${Math.round(file.size / 1024)} KB). The limit is 800 KB.`;
      else {
        try {
          item.text = await readFileText(file);
          item.meta = readHeader(item.text);
          const heading = (item.text.match(/^#{1,3}[ \t]+(.+?)[ \t#]*$/m) || [])[1];
          item.title = item.meta.title || heading || file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ');
        } catch (e) {
          item.error = 'This file could not be read.';
        }
      }
      added.push(item);
    }
    setFiles((all) => [...all, ...added]);
    // Pre-fill the shared fields from a file header, but never overwrite what the person has chosen
    const withMeta = added.filter((a) => !a.error);
    const cat = withMeta.map((a) => categories.find((c) => c.name.toLowerCase() === (a.meta.category || '').toLowerCase() || c.slug === (a.meta.category || '').toLowerCase())).find(Boolean);
    if (cat) setCategory((c) => c || String(cat.id));
    const fromHeaders = cleanTags(withMeta.flatMap((a) => headerTags(a.meta)));
    if (fromHeaders.length) setTags((t) => (t.length ? t : fromHeaders));
  };

  const tagList = cleanTags(tags);
  const waiting = files.filter((f) => !f.error && f.state !== 'done');
  const canUpload = !busy && waiting.length > 0 && !!category && tagList.length >= 1;
  const doneCount = files.filter((f) => f.state === 'done').length;

  const upload = async () => {
    setBusy(true);
    for (const f of files) {
      if (f.error || f.state === 'done') continue;
      patch(f.key, { state: 'uploading', message: null });
      try {
        const res = await kbAPI.create({
          markdown: f.text, filename: f.name, title: f.title.trim() || null, category, tags: tagList, status,
          owner_email: email || null, review_due: reviewDue || null, change_note: `Uploaded ${f.name}`,
        });
        patch(f.key, { state: 'done', id: res.data.id });
      } catch (err) {
        patch(f.key, { state: 'failed', message: errText(err, 'Upload failed') });
      }
    }
    setBusy(false);
  };

  return (
    <Dialog open onClose={() => !busy && onClose(doneCount > 0)} maxWidth="md" fullWidth>
      <DialogTitle>Upload Markdown files</DialogTitle>
      <DialogContent>
        <Paper
          variant="outlined"
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); addFiles(e.dataTransfer.files); }}
          sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed', borderWidth: 2, borderColor: over ? 'primary.main' : 'divider', bgcolor: over ? 'action.hover' : 'transparent', mb: 2 }}
        >
          <Typography gutterBottom>Drop .md files here</Typography>
          <input ref={inputRef} type="file" multiple hidden accept=".md,.markdown,.txt,text/markdown,text/plain" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          <Button variant="outlined" onClick={() => inputRef.current.click()} disabled={busy}>Choose files</Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Up to 800 KB each. A header at the top of a file (title, category, tags between --- lines) fills in the fields below.
          </Typography>
        </Paper>

        {files.map((f) => (
          <Box key={f.key} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 160, flex: '0 0 200px' }}>
              <Typography variant="body2" noWrap title={f.name}>{f.name}</Typography>
              <Typography variant="caption" color="text.secondary">{(f.size / 1024).toFixed(1)} KB</Typography>
            </Box>
            {f.error ? (
              <Alert severity="error" sx={{ py: 0, flex: 1 }}>{f.error}</Alert>
            ) : (
              <TextField size="small" label="Title" value={f.title} sx={{ flex: 1, minWidth: 220 }} disabled={f.state !== 'ready' && f.state !== 'failed'}
                onChange={(e) => patch(f.key, { title: e.target.value })} />
            )}
            {f.state === 'uploading' && <CircularProgress size={20} />}
            {f.state === 'done' && <Chip size="small" color="success" label="Uploaded" />}
            {f.state === 'failed' && <Chip size="small" color="error" label={f.message} sx={{ maxWidth: 320 }} />}
            {(f.state === 'ready' || f.state === 'failed') && !busy && (
              <Button size="small" onClick={() => setFiles((all) => all.filter((x) => x.key !== f.key))}>Remove</Button>
            )}
          </Box>
        ))}
        {busy && <LinearProgress sx={{ mt: 1 }} />}

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Set these for the files above</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField select size="small" label="Category (required)" value={category} onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 200 }} disabled={busy}>
            {categories.filter((c) => c.is_active).map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
          </TextField>
          <Autocomplete
            multiple freeSolo autoSelect size="small" options={tagOptions.filter((t) => !tagList.includes(t))} value={tagList} disabled={busy}
            onChange={(e, value) => setTags(cleanTags(value))}
            sx={{ flex: 1, minWidth: 280 }}
            renderInput={(params) => (
              <TextField {...params} label="Tags (required)" error={files.length > 0 && tagList.length === 0}
                helperText={tagList.length === 0 ? 'Type a tag and press Enter. At least one is needed so people can find the article.' : 'Press Enter after each tag'} />
            )}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }} disabled={busy}>
            <MenuItem value="published">Published</MenuItem><MenuItem value="draft">Draft (only editors see it)</MenuItem>
          </TextField>
          <TextField size="small" type="date" label="Review by (optional)" value={reviewDue} onChange={(e) => setReviewDue(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} disabled={busy} />
        </Box>

        {files.some((f) => f.state === 'done') && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {doneCount} article{doneCount === 1 ? '' : 's'} uploaded.
            {files.filter((f) => f.state === 'done').map((f) => <span key={f.key}> <a href={`/kb/${f.id}`}>{f.title || f.name}</a></span>)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(doneCount > 0)} disabled={busy}>{doneCount > 0 && waiting.length === 0 ? 'Done' : 'Cancel'}</Button>
        <Button variant="contained" disabled={!canUpload} onClick={upload}>
          {busy ? 'Uploading…' : `Upload ${waiting.length || ''} file${waiting.length === 1 ? '' : 's'}`.replace('  ', ' ')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Managing categories (administrators) ───────────────────────────────────

function ManageCategories({ onClose }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await kbAPI.categories();
      setRows(r.data.map((c) => ({ ...c, draftName: c.name })));
    } catch (err) {
      setError(errText(err, 'Could not load the categories'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, message) => {
    setBusy(true); setError(null); setNote(null);
    try { await fn(); if (message) setNote(message); await load(); }
    catch (err) { setError(errText(err, 'That did not work')); }
    finally { setBusy(false); }
  };

  const add = () => run(() => kbAPI.createCategory({ name: newName.trim(), sort_order: Math.max(0, ...rows.map((r) => r.sort_order)) + 10 }).then(() => setNewName('')), `Added ${newName.trim()}`);
  const rename = (row) => run(() => kbAPI.updateCategory(row.id, { name: row.draftName.trim() }), `Renamed to ${row.draftName.trim()}`);
  const toggle = (row) => run(() => kbAPI.updateCategory(row.id, { is_active: !row.is_active }), row.is_active ? `${row.name} hidden from the choices` : `${row.name} is available again`);
  const move = (index, dir) => {
    const order = [...rows];
    const [item] = order.splice(index, 1);
    order.splice(index + dir, 0, item);
    return run(async () => { for (let i = 0; i < order.length; i += 1) { if (order[i].sort_order !== (i + 1) * 10) await kbAPI.updateCategory(order[i].id, { sort_order: (i + 1) * 10 }); } });
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Manage categories</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {note && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNote(null)}>{note}</Alert>}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, pt: 1 }}>
          <TextField size="small" fullWidth label="New category" value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim() && !busy) add(); }} />
          <Button variant="contained" disabled={!newName.trim() || busy} onClick={add}>Add</Button>
        </Box>
        <Table size="small">
          <TableHead><TableRow><TableCell>Name</TableCell><TableCell align="right">Articles</TableCell><TableCell align="center">Shown</TableCell><TableCell align="right">Order</TableCell></TableRow></TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField size="small" value={row.draftName} sx={{ minWidth: 160 }} disabled={busy}
                      onChange={(e) => setRows((all) => all.map((r) => (r.id === row.id ? { ...r, draftName: e.target.value } : r)))} />
                    {row.draftName.trim() && row.draftName.trim() !== row.name && <Button size="small" disabled={busy} onClick={() => rename(row)}>Save</Button>}
                  </Box>
                </TableCell>
                <TableCell align="right">{row.articles}</TableCell>
                <TableCell align="center">
                  <Button size="small" disabled={busy} color={row.is_active ? 'primary' : 'warning'} onClick={() => toggle(row)}>{row.is_active ? 'Shown' : 'Hidden'}</Button>
                </TableCell>
                <TableCell align="right">
                  <Button size="small" disabled={busy || i === 0} onClick={() => move(i, -1)} aria-label={`Move ${row.name} up`}>Up</Button>
                  <Button size="small" disabled={busy || i === rows.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${row.name} down`}>Down</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          The order here is the order of the filter chips and the choices when uploading. Hiding a category removes it from the choices for new
          articles; articles already in it keep it and can still be found. Categories cannot be deleted.
        </Typography>
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={busy}>Done</Button></DialogActions>
    </Dialog>
  );
}

// ── List and search ────────────────────────────────────────────────────────

export function KnowledgeBaseList() {
  const { canEditKb, isAdmin } = usePermissions();
  const navigate = useNavigate();
  const [managing, setManaging] = useState(false);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [searchParams] = useSearchParams();
  const [tag, setTag] = useState(searchParams.get('tag') || '');
  const [view, setView] = useState(searchParams.get('view') || '');   // '', 'review', 'archived'
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    kbAPI.categories().then((r) => setCategories(r.data)).catch(() => {});
    kbAPI.tags().then((r) => setTags(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (category) params.category = category;
      if (tag) params.tag = tag;
      if (view === 'review') params.review = 'due';
      if (view === 'archived') params.status = 'archived';
      const res = await kbAPI.articles(params);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(errText(err, 'Failed to load articles'));
    } finally {
      setLoading(false);
    }
  }, [q, category, tag, view]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Knowledge Base</Typography>
        {canEditKb() && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {isAdmin() && <Button onClick={() => setManaging(true)}>Manage categories</Button>}
            <Button variant="contained" onClick={() => setUploading(true)}>Upload .md files</Button>
            <Button variant="outlined" onClick={() => setCreating(true)}>Write an article</Button>
          </Box>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Procedures and how-to articles for the team</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField fullWidth size="small" label="Search articles" value={q} onChange={(e) => setQ(e.target.value)}
          helperText='Words, "exact phrases" and -exclude all work' />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, alignItems: 'center' }}>
          <Chip label="All" color={!category ? 'primary' : 'default'} onClick={() => setCategory('')} />
          {categories.filter((c) => c.is_active).map((c) => (
            <Chip key={c.id} label={`${c.name} (${c.articles})`} color={category === c.slug ? 'primary' : 'default'}
              onClick={() => setCategory(category === c.slug ? '' : c.slug)} />
          ))}
          <Box sx={{ flexGrow: 1 }} />
          {canEditKb() && (
            <>
              <Chip size="small" variant={view === 'review' ? 'filled' : 'outlined'} color="warning" label="Due for review"
                onClick={() => setView(view === 'review' ? '' : 'review')} />
              <Chip size="small" variant={view === 'archived' ? 'filled' : 'outlined'} label="Archived"
                onClick={() => setView(view === 'archived' ? '' : 'archived')} />
            </>
          )}
        </Box>
        {tags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
            {tags.slice(0, 20).map((t) => (
              <Chip key={t.tag} size="small" variant={tag === t.tag ? 'filled' : 'outlined'} label={`#${t.tag}`}
                onClick={() => setTag(tag === t.tag ? '' : t.tag)} />
            ))}
          </Box>
        )}
      </Paper>

      {loading && data.items.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">{data.total} article{data.total === 1 ? '' : 's'}</Typography>
          {data.items.map((a) => (
            <Paper key={a.id} sx={{ p: 2, mt: 1.5, cursor: 'pointer', '&:hover': { boxShadow: 3 } }} onClick={() => navigate(`/kb/${a.id}`)}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ mr: 1 }}>{a.title}</Typography>
                <Chip size="small" label={a.category.name} color="primary" variant="outlined" />
                {a.status !== 'published' && <Chip size="small" label={a.status} color="warning" />}
                {a.status === 'published' && isOverdue(a.review_due) && <Chip size="small" label="Review due" color="error" />}
              </Box>
              {a.snippet ? <Snippet text={a.snippet} /> : a.summary && <Typography variant="body2" color="text.secondary">{a.summary}</Typography>}
              <Typography variant="caption" color="text.secondary">
                Updated {fmtDate(a.updated_at)} by {a.updated_by}{a.tags.length ? ` · ${a.tags.map((t) => `#${t}`).join(' ')}` : ''}
              </Typography>
            </Paper>
          ))}
          {data.items.length === 0 && <Alert severity="info" sx={{ mt: 2 }}>No articles match. Try different words or clear the filters.</Alert>}
        </>
      )}

      {managing && (
        <ManageCategories onClose={() => { setManaging(false); kbAPI.categories().then((r) => setCategories(r.data)).catch(() => {}); }} />
      )}

      {uploading && (
        <UploadDialog categories={categories} onClose={(changed) => {
          setUploading(false);
          if (changed) {
            load();
            kbAPI.categories().then((r) => setCategories(r.data)).catch(() => {});
            kbAPI.tags().then((r) => setTags(r.data)).catch(() => {});
          }
        }} />
      )}

      {creating && (
        <ArticleEditor onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); navigate(`/kb/${id}`); }} categories={categories} />
      )}
    </Box>
  );
}

// ── One article ────────────────────────────────────────────────────────────

export function KnowledgeBaseArticle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEditKb } = usePermissions();
  const [a, setA] = useState(null);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await kbAPI.article(id);
      setA(res.data);
      setError(null);
    } catch (err) {
      setError(errText(err, 'Could not load the article'));
    }
  }, [id]);

  useEffect(() => { load(); kbAPI.categories().then((r) => setCategories(r.data)).catch(() => {}); }, [load]);

  const setStatus = async (status) => {
    try { await kbAPI.update(id, { status }); setSuccess(`Article ${status === 'archived' ? 'archived' : status === 'draft' ? 'moved to draft' : 'published'}`); load(); }
    catch (err) { setError(errText(err, 'Could not change the status')); }
  };

  if (error && !a) return <Alert severity="error" action={<Button onClick={() => navigate('/kb')}>Back</Button>}>{error}</Alert>;
  if (!a) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Button size="small" onClick={() => navigate('/kb')} sx={{ mb: 1 }}>← All articles</Button>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          <Chip size="small" label={a.category.name} color="primary" variant="outlined" />
          {a.tags.map((t) => <Chip key={t} size="small" variant="outlined" label={`#${t}`} onClick={() => navigate(`/kb?tag=${t}`)} />)}
          {a.status !== 'published' && <Chip size="small" color="warning" label={a.status} />}
          {a.status === 'published' && isOverdue(a.review_due) && <Chip size="small" color="error" label="Review due" />}
          <Box sx={{ flexGrow: 1 }} />
          {canEditKb() && (
            <>
              <Button size="small" variant="contained" onClick={() => setEditing(true)}>Edit</Button>
              <Button size="small" onClick={() => setHistory(true)}>History</Button>
              {a.status === 'draft' && <Button size="small" onClick={() => setStatus('published')}>Publish</Button>}
              {a.status === 'published' && <Button size="small" onClick={() => setStatus('draft')}>Unpublish</Button>}
              {a.status !== 'archived'
                ? <Button size="small" color="warning" onClick={() => setStatus('archived')}>Archive</Button>
                : <Button size="small" onClick={() => setStatus('published')}>Restore</Button>}
            </>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Owner {a.owner_email} · Version {a.current_version} · Updated {fmtTime(a.updated_at)} by {a.updated_by}
          {a.review_due ? ` · Review by ${fmtDate(a.review_due)}` : ''}
        </Typography>
        <Markdown text={a.body} articleId={a.id} />
      </Paper>

      {editing && (
        <ArticleEditor article={a} categories={categories} onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); setSuccess('Saved'); load(); }} />
      )}
      {history && <History article={a} onClose={() => setHistory(false)} onRestored={() => { setHistory(false); setSuccess('Version restored as a new version'); load(); }} />}
    </Box>
  );
}

// ── Version history ────────────────────────────────────────────────────────

function History({ article, onClose, onRestored }) {
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);

  const open = async (n) => {
    try { setView((await kbAPI.version(article.id, n)).data); } catch (err) { setError(errText(err, 'Could not load that version')); }
  };
  const restore = async (n) => {
    try { await kbAPI.restore(article.id, n); onRestored(); } catch (err) { setError(errText(err, 'Could not restore')); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>History: {article.title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Table size="small">
          <TableHead><TableRow><TableCell>Version</TableCell><TableCell>When</TableCell><TableCell>Who</TableCell><TableCell>Note</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {(article.versions || []).map((v) => (
              <TableRow key={v.version}>
                <TableCell>{v.version}{v.version === article.current_version ? ' (current)' : ''}</TableCell>
                <TableCell>{fmtTime(v.created_at)}</TableCell>
                <TableCell>{v.created_by}</TableCell>
                <TableCell>{v.change_note}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => open(v.version)}>View</Button>
                  {v.version !== article.current_version && <Button size="small" onClick={() => restore(v.version)}>Restore</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {view && (
          <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Version {view.version}</Typography>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: 13, m: 0, maxHeight: 360, overflow: 'auto' }}>{view.markdown}</Box>
          </Paper>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── Writing and uploading ──────────────────────────────────────────────────

function readFileText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.readAsText(file);
  });
}

// Pull title / category / tags out of the optional header at the top of a file, to pre-fill the form
function readHeader(text) {
  const m = text.match(/^---[ \t]*\n([\s\S]*?)\n---[ \t]*(\n|$)/);
  const meta = {};
  if (m) m[1].split('\n').forEach((line) => { const i = line.indexOf(':'); if (i > 0) meta[line.slice(0, i).trim().toLowerCase()] = line.slice(i + 1).trim().replace(/^["']|["']$/g, ''); });
  return meta;
}

function ArticleEditor({ article, categories, onClose, onSaved }) {
  const { email } = usePermissions();
  const isNew = !article;
  const [form, setForm] = useState({
    title: article?.title || '', category: article ? String(article.category.id) : '', tags: (article?.tags || []).join(', '),
    status: article?.status || 'published', owner_email: article?.owner_email || email || '', review_due: article?.review_due ? String(article.review_due).slice(0, 10) : '',
    summary: article?.summary || '', change_note: '',
  });
  const [markdown, setMarkdown] = useState(article?.markdown || '');
  const [filename, setFilename] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);
  const fileRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => { usersAPI.list().then((r) => setUsers(r.data.filter((u) => u.is_active))).catch(() => {}); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await readFileText(file);
      const meta = readHeader(text);
      const cat = categories.find((c) => c.name.toLowerCase() === (meta.category || '').toLowerCase() || c.slug === (meta.category || '').toLowerCase());
      setMarkdown(text);
      setFilename(file.name);
      setForm((f) => ({
        ...f,
        title: f.title || meta.title || '',
        category: f.category || (cat ? String(cat.id) : ''),
        tags: f.tags || (meta.tags || '').replace(/[[\]]/g, ''),
      }));
      setNote(`Loaded ${file.name}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !article) return;
    try {
      const res = await kbAPI.uploadAsset(article.id, file);
      setMarkdown((m) => `${m}${m.endsWith('\n') ? '' : '\n'}\n${res.data.markdown}\n`);
      setNote(`Added ${res.data.filename} at the end of the text`);
    } catch (err) {
      setError(errText(err, 'Could not add the image'));
    }
  };

  const save = async () => {
    setSaving(true); setError(null);
    const tags = form.tags.split(',').map((t) => t.trim()).filter(Boolean);
    try {
      if (isNew) {
        const res = await kbAPI.create({
          markdown, filename, title: form.title || null, summary: form.summary || null, category: form.category || null,
          tags, status: form.status, owner_email: form.owner_email || null, review_due: form.review_due || null, change_note: form.change_note || null,
        });
        onSaved(res.data.id);
      } else {
        await kbAPI.update(article.id, {
          markdown, title: form.title, summary: form.summary, category: form.category, tags, status: form.status,
          owner_email: form.owner_email, review_due: form.review_due || null, clear_review_due: !form.review_due, change_note: form.change_note || null,
        });
        onSaved(article.id);
      }
    } catch (err) {
      setError(errText(err, 'Could not save the article'));
      setSaving(false);
    }
  };

  const hasTags = form.tags.split(',').map((t) => t.trim()).filter(Boolean).length > 0 || headerTags(readHeader(markdown)).length > 0;
  const ready = markdown.trim() && hasTags && (form.title.trim() || /^#\s+\S/m.test(markdown) || readHeader(markdown).title) && (form.category || readHeader(markdown).category);

  return (
    <Dialog open fullScreen onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isNew ? 'New article' : `Edit: ${article.title}`}
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
          <Button variant="contained" disabled={!ready || saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {note && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNote(null)}>{note}</Alert>}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, pt: 1 }}>
          <TextField size="small" label="Title" value={form.title} onChange={set('title')} sx={{ minWidth: 320, flex: 1 }}
            helperText={isNew ? 'Or leave blank to use the first # heading' : ''} />
          <TextField select size="small" label="Category" value={form.category} onChange={set('category')} sx={{ minWidth: 180 }}>
            {categories.filter((c) => c.is_active || String(c.id) === form.category).map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.name}{c.is_active ? '' : ' (hidden)'}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Tags (required)" value={form.tags} onChange={set('tags')} sx={{ minWidth: 240 }}
            error={!hasTags} helperText="Comma separated, at least one" />
          <TextField select size="small" label="Status" value={form.status} onChange={set('status')} sx={{ minWidth: 140 }}>
            <MenuItem value="published">Published</MenuItem><MenuItem value="draft">Draft</MenuItem>
            {!isNew && <MenuItem value="archived">Archived</MenuItem>}
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField select size="small" label="Owner" value={form.owner_email} onChange={set('owner_email')} sx={{ minWidth: 280 }}>
            {users.map((u) => <MenuItem key={u.id} value={u.email}>{u.name ? `${u.name} (${u.email})` : u.email}</MenuItem>)}
          </TextField>
          <TextField size="small" type="date" label="Review by" value={form.review_due} onChange={set('review_due')} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" label="Summary (optional)" value={form.summary} onChange={set('summary')} sx={{ minWidth: 320, flex: 1 }} />
          <TextField size="small" label={isNew ? 'Note (optional)' : 'What changed?'} value={form.change_note} onChange={set('change_note')} sx={{ minWidth: 240 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <input ref={fileRef} type="file" accept=".md,.markdown,.txt,text/markdown,text/plain" hidden onChange={onFile} />
          <Button size="small" variant="outlined" onClick={() => fileRef.current.click()}>Load a .md file</Button>
          <input ref={imageRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={onImage} />
          <Button size="small" variant="outlined" disabled={isNew} onClick={() => imageRef.current.click()}>Add an image</Button>
          {isNew && <Typography variant="caption" color="text.secondary">Save the article first, then you can add images.</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{(new Blob([markdown]).size / 1024).toFixed(1)} KB of 800 KB</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField multiline minRows={22} maxRows={40} fullWidth value={markdown} onChange={(e) => setMarkdown(e.target.value)}
            placeholder={'# Title\n\nWrite in Markdown, or load a .md file above.'}
            slotProps={{ input: { sx: { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13 } } }} />
          <Paper variant="outlined" sx={{ p: 2, overflow: 'auto', maxHeight: 700 }}>
            <Typography variant="caption" color="text.secondary">Preview</Typography>
            <Markdown text={markdown.replace(/^---[ \t]*\n[\s\S]*?\n---[ \t]*(\n|$)/, '')} articleId={article?.id} />
          </Paper>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default KnowledgeBaseList;
